/**
 * @fileoverview Shared import run-flow wizard (upload → map → match-key →
 *               dry-run → commit) hosted by UnifiedImportWizard's run intent.
 * @module import-editor/runFlow/UnifiedImportRunFlow
 *
 * Responsibilities:
 * - Compose the run-flow chrome (header, stepper, step body, footer) around
 *   the `useImportRunFlow` state machine and the shared step components.
 * - Expose host-specific behavior through explicit options and render slots
 *   (spatial filter, chrome accessories) — never through host imports.
 *
 * Dependencies:
 * - `./useImportRunFlow` — the shared run-flow state machine.
 * - `./steps/*` — the per-step bodies.
 * - `./MatchKeyStepContent` — the shared match-key step.
 * - `../unifiedWizard/services` — the host services adapter contract.
 *
 * Architecture:
 * - Composition layer only: no business logic lives here; the hook owns the
 *   state machine and the adapter owns I/O. Hosts wire tenant context,
 *   routing, and navigation from their page components.
 * - Retires tenant-web's ImportWizardContainer and system-web's
 *   SystemImportWizardContainer (Phase 2A, G-002): the run-flow state
 *   machine now exists exactly once, here.
 *
 * Notes:
 * - Chrome differences between hosts (title/counter block, step
 *   descriptions, free vs back-only stepper navigation, Cancel button,
 *   footer visibility on the commit step) are presentation-only and driven
 *   by the `chrome` prop.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import type {
  ImportTemplateSampleBlob,
  ImportTemplateSamplePreview,
} from '@firesquirrel/local-db';
import type { ImportWizardServices } from '../unifiedWizard/services';
import { useImportRunFlow } from './useImportRunFlow';
import type { RunStepId } from './navigation';
import RunUploadStep, { type SpatialFilterSlotProps } from './steps/RunUploadStep';
import RunColumnMappingStep from './steps/RunColumnMappingStep';
import RunMatchKeyStepContent from './MatchKeyStepContent';
import RunDryRunStep from './steps/RunDryRunStep';
import RunCommitStep from './steps/RunCommitStep';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * @contract RunFlowChrome
 *
 * Purpose:
 * - Presentation-only chrome configuration for the run flow. All behavioral
 *   differences live in the hook options / adapter; these flags only change
 *   what the header, stepper, and footer render.
 *
 * Fields:
 * - title: optional header title block (tenant-web: 'Data Import Wizard').
 * - showStepCounter: render "Step N of M: label" under the title.
 * - showStepDescriptions: render the active step's description above the body.
 * - headerAccessory: host-rendered node at the right of the title row.
 * - showTemplateBanner: show the selected-template chip in the stepper row.
 * - freeStepNavigation: allow clicking any step pill (tenant-web). When
 *   false, only completed steps are clickable (system-web).
 * - backLabel: footer back-button label ('Previous' vs 'Back').
 * - hideFooterOnCommit: hide Back/Next on the commit step (tenant-web).
 * - commitHistoryAccessory: host-rendered history link on commit success.
 */
export interface RunFlowChrome {
  title?: string;
  showStepCounter?: boolean;
  showStepDescriptions?: boolean;
  headerAccessory?: ReactNode;
  showTemplateBanner?: boolean;
  freeStepNavigation?: boolean;
  backLabel?: string;
  hideFooterOnCommit?: boolean;
  commitHistoryAccessory?: ReactNode;
}

/**
 * @contract UnifiedImportRunFlowProps
 *
 * Purpose:
 * - Configure the shared import run flow for a host.
 *
 * Fields:
 * - services: host adapter implementing ImportWizardServices.
 * - initialStepId/initialTemplateId: deep-link entry points.
 * - initialFile/initialSampleBlob/initialSamplePreview: Card 3 handoff.
 * - enableManualModePicker / renderSpatialFilter: tenant-web upload-step extras.
 * - restorePreviewSession: tenant-web preview-session resume.
 * - draftRunPersistence: system-web draft-run persistence + launch resume.
 * - requireFullSyncDeleteConfirm: tenant-web full-sync commit gate.
 * - defaultTemplateVisibility: system-web visibility for run-created templates.
 * - fileInputId: DOM id for the hidden file input (E2E suites pin it).
 * - hideTemplatePickerWhenSeeded: system-web hides the picker on deep-links.
 * - chrome: presentation-only chrome configuration.
 * - onComplete/onCancel: host navigation callbacks.
 *
 * Rules:
 * - All data access flows through `services`; this component never fetches.
 *
 * Sync:
 * - Draft-run / preview-session persistence is mediated by the adapter's
 *   local-first services; upload/parse/dry-run/commit are the documented
 *   online-only exceptions.
 */
export interface UnifiedImportRunFlowProps {
  services: ImportWizardServices;
  initialStepId?: RunStepId;
  initialTemplateId?: string;
  initialFile?: File | null;
  initialSampleBlob?: ImportTemplateSampleBlob;
  initialSamplePreview?: ImportTemplateSamplePreview;
  enableManualModePicker?: boolean;
  renderSpatialFilter?: (props: SpatialFilterSlotProps) => ReactNode;
  restorePreviewSession?: boolean;
  draftRunPersistence?: { resumeStorageKey: string } | null;
  requireFullSyncDeleteConfirm?: boolean;
  defaultTemplateVisibility?: string;
  fileInputId?: string;
  hideTemplatePickerWhenSeeded?: boolean;
  chrome?: RunFlowChrome;
  onComplete?: () => void;
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component UnifiedImportRunFlow
 *
 * Purpose:
 * - Render the complete import run flow for the `new-import` intent.
 *
 * Props:
 * - UnifiedImportRunFlowProps.
 *
 * Behavior:
 * - Drives the five-step pipeline via `useImportRunFlow`; the stepper
 *   respects lookup-mode match-key skipping via the shared navigation rules.
 *
 * Data Source:
 * - All state from the hook; all I/O via the services adapter.
 *
 * Constraints:
 * - No direct API calls, no Dexie access, no host imports.
 *
 * Performance:
 * - Renders only the active step body; the stepper/footer are static weight.
 */
export default function UnifiedImportRunFlow(props: UnifiedImportRunFlowProps) {
  const {
    services,
    initialStepId,
    initialTemplateId,
    initialFile,
    initialSampleBlob,
    initialSamplePreview,
    enableManualModePicker = false,
    renderSpatialFilter,
    restorePreviewSession = false,
    draftRunPersistence = null,
    requireFullSyncDeleteConfirm = false,
    defaultTemplateVisibility,
    fileInputId = 'import-file-input',
    hideTemplatePickerWhenSeeded = false,
    chrome,
    onComplete,
    onCancel,
  } = props;

  const flow = useImportRunFlow({
    services,
    initialStepId,
    initialTemplateId,
    initialFile,
    initialSampleBlob,
    initialSamplePreview,
    enableManualModePicker,
    restorePreviewSession,
    draftRunPersistence,
  });

  const {
    state,
    visibleSteps,
    activeStepId,
    setActiveStepId,
    currentIndex,
    canGoBack,
    canGoNext,
  } = flow;

  const freeStepNavigation = chrome?.freeStepNavigation ?? true;
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Scroll the content area back to the top on every step change. Guarded
  // for jsdom, which does not implement Element.scrollTo.
  useEffect(() => {
    const el = contentScrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [activeStepId]);

  // Suppress the browser's default file-drop behavior (which opens the file
  // or triggers a Save-as dialog) while the wizard is mounted. The drop zone
  // prevents default for drops INSIDE it; attaching these listeners at the
  // window level keeps an accidental drop anywhere else on the page inert.
  useEffect(() => {
    const swallow = (e: DragEvent) => { e.preventDefault(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const currentStep = visibleSteps[currentIndex];
  const isCommitStep = activeStepId === 'commit';
  const hideFooter = isCommitStep && (chrome?.hideFooterOnCommit ?? false);

  const renderStep = () => {
    switch (activeStepId) {
      case 'upload':
        return (
          <RunUploadStep
            flow={flow}
            enableManualModePicker={enableManualModePicker}
            renderSpatialFilter={renderSpatialFilter}
            hideTemplatePicker={hideTemplatePickerWhenSeeded && !!initialTemplateId}
            fileInputId={fileInputId}
          />
        );
      case 'columnMapping':
        return <RunColumnMappingStep flow={flow} />;
      case 'matchKey':
        // Shared run-intent MatchKey step. Auto-renders the lookup-mode
        // info panel when dataMode === 'lookup'.
        return (
          <RunMatchKeyStepContent
            fieldMappings={state.fieldMappings}
            dataMode={state.dataMode ?? 'parcel'}
            importBehavior={state.importBehavior}
            onBehaviorChange={b => flow.patch({ importBehavior: b })}
          />
        );
      case 'preview':
        return <RunDryRunStep flow={flow} />;
      case 'commit':
        return (
          <RunCommitStep
            flow={flow}
            requireFullSyncDeleteConfirm={requireFullSyncDeleteConfirm}
            defaultTemplateVisibility={defaultTemplateVisibility}
            commitHistoryAccessory={chrome?.commitHistoryAccessory}
            onComplete={onComplete}
          />
        );
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white flex flex-col h-full overflow-hidden shadow-xs">
      {/* Optional title row (tenant-web chrome) */}
      {chrome?.title && (
        <div className="shrink-0 px-6 pt-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{chrome.title}</h2>
            {chrome.showStepCounter && currentStep && (
              <p className="text-sm text-gray-500">Step {currentIndex + 1} of {visibleSteps.length}: {currentStep.label}</p>
            )}
          </div>
          {chrome.headerAccessory}
        </div>
      )}

      {/* Stepper + optional selected-template banner — pinned at top */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-6 py-3 mt-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {visibleSteps.map((step, i) => (
            <div key={step.id} className="flex items-center gap-2">
              {i > 0 && <div className="w-4 h-px bg-gray-300" />}
              <button
                type="button"
                onClick={() => (freeStepNavigation || i <= currentIndex) && setActiveStepId(step.id)}
                className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 transition-colors ${
                  step.id === activeStepId
                    ? 'bg-orange-600 text-white'
                    : i < currentIndex
                      ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200'
                      : 'bg-gray-100 text-gray-400 cursor-default'
                }`}
              >
                <span>{i + 1}</span>
                <span>{step.label}</span>
              </button>
            </div>
          ))}
        </div>
        {chrome?.showTemplateBanner && state.selectedTemplate && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Template:</span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-800 rounded-full font-medium">
              {state.selectedTemplate.name}
            </span>
          </div>
        )}
      </div>

      {/* Scrollable content area */}
      <div ref={contentScrollRef} className="flex-1 overflow-auto px-6 py-5">
        {chrome?.showStepDescriptions && currentStep?.description && (
          <p className="text-sm text-gray-500 mb-4">{currentStep.description}</p>
        )}
        {renderStep()}
      </div>

      {/* Sticky footer */}
      {!hideFooter && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between">
          <div>
            {onCancel && (
              <button type="button" onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={flow.goBack}
              disabled={!canGoBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {chrome?.backLabel ?? 'Previous'}
            </button>
            <button
              type="button"
              onClick={canGoNext ? flow.goNext : onComplete}
              disabled={canGoNext && (!flow.canAdvance() || flow.uploading)}
              className="px-5 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {flow.nextLabel()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
